import { useEffect, useState } from "react";
import { ButtonPopup, ButtonSiguiente, Input, Select } from "@/components/ui";
import { safeFetch } from "@/api/safeFetch";
import { useNavigate } from "react-router-dom";
import { buildDocumentUrl } from "@/api/buildImageUrl";
import * as XLSX from "xlsx";


// Tipos
type Factura = {
    factura_id: number;
    profesional_id: number | null;
    perfil_profesional_id: number | null;
    usuario_id: number | null;
    stripe_intent_id: string | null;
    cantidad: number; // total con IVA
    base?: number;    // base imponible
    iva?: number;     // IVA desglosado
    fecha: string;    // ISO
    concepto: string | null;
    metodo_pago: string | null;
    estado: "pendiente" | "pagada" | "anulada" | string;
    factura_pdf: string | null;
    numero_factura: string | null;
    card_last4?: string | null;
    card_brand?: string | null;
    payment_method_id?: string | null;
    profesional_nombre?: string | null;
    card_holder_name?: string | null;
};



export default function FacturasResumen() {
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    const [facturas, setFacturas] = useState<Factura[]>([]);
    const [loading, setLoading] = useState(false);
    const [, setError] = useState<string | null>(null);

    const [month, setMonth] = useState<string>("");
    const [fechaInicio, setFechaInicio] = useState<string>("");
    const [fechaFin, setFechaFin] = useState<string>("");
    const [estadoFiltro, setEstadoFiltro] = useState<string>("");

    const [total, setTotal] = useState<number>(0);
    const [totalBase, setTotalBase] = useState<number>(0);
    const [totalIVA, setTotalIVA] = useState<number>(0);

    const [page, setPage] = useState(1);
    const [perPage] = useState(50);

    // Función para cargar facturas
    const fetchFacturas = async (opts?: { page?: number; signal?: AbortSignal }) => {
        setError(null);
        setLoading(true);

        try {
            let url = "/api/admin/facturas";
            const params: string[] = [];

            if (month) params.push(`month=${encodeURIComponent(month)}`);
            else if (fechaInicio && fechaFin) {
                params.push(`start=${encodeURIComponent(`${fechaInicio}T00:00:00Z`)}`);
                params.push(`end=${encodeURIComponent(`${fechaFin}T23:59:59Z`)}`);
            }

            if (estadoFiltro) params.push(`estado=${encodeURIComponent(estadoFiltro)}`);

            params.push(`page=${opts?.page ?? page}`);
            params.push(`perPage=${perPage}`);

            if (params.length) url += `?${params.join("&")}`;

            const res = await safeFetch(url, {
                headers: { Authorization: `Bearer ${token}` },
                signal: opts?.signal
            });

            if (!res.ok) {
                if (res.status === 403) {
                    setError("No tienes permisos para ver facturas");
                    return;
                }
                throw new Error("Error al cargar facturas");
            }

            const data = await res.json();

            const items: Factura[] = (data.data || []).map((f: any) => {
                const cantidad = Number(f.cantidad || 0);
                const base = cantidad / 1.21;
                const iva = cantidad - base;
                return { ...f, cantidad, base, iva };
            });

            setFacturas(items);

            setTotal(items.reduce((acc, f) => acc + (f.cantidad || 0), 0));
            setTotalBase(items.reduce((acc, f) => acc + (f.base || 0), 0));
            setTotalIVA(items.reduce((acc, f) => acc + (f.iva || 0), 0));

            setPage(data.page || 1);

        } catch (err: any) {
            if (err.name !== "AbortError") {
                setError(err.message || "Error desconocido");
                setFacturas([]);
                setTotal(0);
                setTotalBase(0);
                setTotalIVA(0);
            }
        } finally {
            setLoading(false);
        }
    };


    const exportExcel = () => {
        if (!facturas.length) return;

        const data = facturas.map(f => ({
            "ID": f.factura_id,
            "Nº Factura": f.numero_factura ?? "",
            "Fecha": new Date(f.fecha).toLocaleString(),

            "Profesional": f.profesional_nombre ?? "",

            "Concepto": f.concepto ?? "",

            "Método pago": f.card_brand && f.card_last4
                ? `${f.card_brand.toUpperCase()} **** ${f.card_last4}`
                : f.metodo_pago ?? "",

            "Titular tarjeta": f.card_holder_name ?? "",

            "Estado": f.estado,

            "Base €": Number((f.base ?? 0).toFixed(2)),
            "IVA €": Number((f.iva ?? 0).toFixed(2)),
            "Total €": Number((f.cantidad ?? 0).toFixed(2)),

            "Card brand": f.card_brand ?? "",
            "Card last4": f.card_last4 ?? "",
            "Payment method ID": f.payment_method_id ?? "",
        }));

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Facturas");

        const fileName =
            `facturas_${month || `${fechaInicio}_${fechaFin}` || "export"}.xlsx`;

        XLSX.writeFile(workbook, fileName);
    };


    // Mostrar facturas
    const handleMostrar = async () => {
        setPage(1);
        await fetchFacturas({ page: 1 });
    };

    // Exportar CSV con base e IVA
    const exportCsv = () => {
        if (!facturas.length) return;
        const headers = [
            "factura_id",
            "numero_factura",

            "profesional_nombre",

            "fecha",
            "base",
            "iva",
            "cantidad",

            "concepto",
            "metodo_pago",

            "card_holder_name",

            "estado",
            "factura_pdf",
            "stripe_intent_id",

            "card_brand",
            "card_last4",
            "payment_method_id"
        ];
        const rows = facturas.map(f =>
            headers.map(h => {
                const v = (f as any)[h];
                if (v === null || v === undefined) return "";
                return typeof v === "string" && v.includes(",") ? `"${v.replace(/"/g, '""')}"` : String(v);
            }).join(",")
        );
        const csv = [headers.join(","), ...rows].join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `facturas_${month || `${fechaInicio}_${fechaFin}` || "export"}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    useEffect(() => {
        const controller = new AbortController();
        // Cargar facturas al entrar si quieres
        // fetchFacturas({ page: 1, signal });

        return () => {
            controller.abort();

            // limpiar estados
            setFacturas([]);
            setLoading(false);
            setError(null);
            setMonth("");
            setFechaInicio("");
            setFechaFin("");
            setEstadoFiltro("");
            setTotal(0);
            setTotalBase(0);
            setTotalIVA(0);
            setPage(1);

            // evitar scroll congelado
            window.scrollTo(0, 0);
        };
    }, []);


    return (
        <div className="w-full h-screen flex justify-center bg-zinc-500 p-4 relative text-zinc-800 py-8">

            {/* 🔙 Botón volver arriba izquierda */}
            <div className="absolute top-4 left-4">
                <ButtonSiguiente
                    onClick={() => navigate("/backoffice")}
                    className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition shadow"
                >
                    ⬅ Volver
                </ButtonSiguiente>
            </div>

            <div className="w-full max-w-3xl bg-zinc-400 p-4 sm:p-6 rounded-xl shadow-md border-zinc-600 border-r-4 border-t-2 shadow-cyan-700 flex flex-col gap-6 h-[calc(90vh-4rem)">
                <h2 className="text-xl sm:text-2xl text-center font-bold">Resumen de Facturas</h2>

                {/* Controles */}
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
                    <div className="md:col-span-2 relative flex-1">

                        <label className="text-sm block mb-1">Mes</label>
                        <Input type="month" value={month} onChange={e => { setMonth(e.target.value); setFechaInicio(""); setFechaFin(""); }} className="h-10 px-3 rounded appearance-none w-full" />
                        {/* Ícono personalizado */}
                        <span className="absolute right-2 top-2/3 -translate-y-1/2 pointer-events-none text-gray-500">
                            📅
                        </span>

                    </div>
                    <div className="md:col-span-2 relative flex-1">
                        <label className="text-sm block mb-1">Rango inicio</label>
                        <Input type="date" value={fechaInicio} onChange={e => { setFechaInicio(e.target.value); setMonth(""); }} className="h-10 px-3 rounded w-full" />
                        <span className="absolute right-2 top-2/3 -translate-y-1/2 pointer-events-none text-gray-500">
                            📅
                        </span>
                    </div>
                    <div className="md:col-span-2 relative flex-1">
                        <label className="text-sm block mb-1">Rango fin</label>
                        <Input type="date" value={fechaFin} onChange={e => { setFechaFin(e.target.value); setMonth(""); }} className="h-10 px-3 rounded w-full" />
                        <span className="absolute right-2 top-2/3 -translate-y-1/2 pointer-events-none text-gray-500">
                            📅
                        </span>
                    </div>
                    <div className="md:col-span-2">
                        <label className="text-sm block mb-1">Estado</label>
                        <Select value={estadoFiltro} onChange={e => setEstadoFiltro(e.target.value)} className="h-10 w-full rounded border px-2 bg-white" placeholder="Todos" options={[
                            { value: "", label: "Todos" }, { value: "pagada", label: "Pagada" },
                            { value: "pendiente", label: "Pendiente" }, { value: "anulada", label: "Anulada" }
                        ]} />
                    </div>
                    <div className="md:col-span-3 flex gap-2">
                        <ButtonSiguiente onClick={handleMostrar} className="!h-10 w-full bg-green-600 hover:bg-green-700" disabled={loading || (!month && !(fechaInicio && fechaFin))}>
                            {loading ? "Cargando..." : "Mostrar"}
                        </ButtonSiguiente>
                        <ButtonSiguiente onClick={exportCsv} className="!h-10 w-full bg-blue-600 hover:bg-blue-700" disabled={facturas.length === 0}>Exportar CSV</ButtonSiguiente>
                        <ButtonSiguiente
                            onClick={exportExcel}
                            className="!h-10 w-full bg-emerald-600 hover:bg-emerald-700"
                            disabled={facturas.length === 0}
                        >
                            Exportar Excel
                        </ButtonSiguiente>
                    </div>
                </div>

                {/* Tabla */}
                <div className="bg-zinc-300 p-2 rounded-lg border shadow overflow-x-auto">
                   <div className="min-w-[2600px]">

                        <table className="w-full table-fixed text-sm border-collapse">

                            <thead>
                                <tr className="bg-blue-700 text-white">

                                    <th className="w-[60px] p-2">Id</th>

                                    <th className="w-[140px] p-2">
                                        Nº Factura
                                    </th>

                                    <th className="w-[180px] p-2">
                                        Fecha
                                    </th>

                                    <th className="w-[220px] p-2">
                                        Profesional
                                    </th>

                                    <th className="w-[120px] p-2 text-center">
                                        Profesional ID
                                    </th>

                                    <th className="w-[120px] p-2 text-center">
                                        Perfil ID
                                    </th>

                                    <th className="w-[120px] p-2 text-center">
                                        Usuario ID
                                    </th>

                                    <th className="w-[260px] p-2">
                                        Concepto
                                    </th>

                                    <th className="w-[220px] p-2">
                                        Método pago
                                    </th>

                                    <th className="w-[220px] p-2">
                                        Titular tarjeta
                                    </th>

                                    <th className="w-[180px] p-2">
                                        Payment Method
                                    </th>

                                    <th className="w-[120px] p-2">
                                        Brand
                                    </th>

                                    <th className="w-[100px] p-2">
                                        Last4
                                    </th>

                                    <th className="w-[180px] p-2 text-center">
                                        Stripe Intent
                                    </th>

                                    <th className="w-[120px] p-2 text-center">
                                        Estado
                                    </th>

                                    <th className="w-[110px] p-2 text-right">
                                        Base €
                                    </th>

                                    <th className="w-[110px] p-2 text-right">
                                        IVA €
                                    </th>

                                    <th className="w-[120px] p-2 text-right">
                                        Total €
                                    </th>

                                    <th className="w-[100px] p-2 text-center">
                                        PDF
                                    </th>

                                </tr>
                            </thead>

                            <tbody>
                                {facturas.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={12}
                                            className="p-4 text-center text-gray-600"
                                        >
                                            {loading
                                                ? "Cargando facturas..."
                                                : "No hay facturas para el periodo seleccionado"}
                                        </td>
                                    </tr>
                                ) : (
                                    facturas.map((f) => (
                                        <tr
                                            key={f.factura_id}
                                            className="border-t hover:bg-zinc-100 align-top"
                                        >
                                            <td className="p-2 text-center">
                                                {f.factura_id}
                                            </td>

                                            <td className="p-2 truncate">
                                                {f.numero_factura || "—"}
                                            </td>

                                            <td className="p-2 truncate">
                                                {new Date(f.fecha).toLocaleString()}
                                            </td>

                                            <td className="p-2 truncate">
                                                {f.profesional_nombre ?? "—"}
                                            </td>

                                            <td className="p-2 text-center">
                                                {f.profesional_id ?? "—"}
                                            </td>

                                            <td className="p-2 text-center">
                                                {f.perfil_profesional_id ?? "—"}
                                            </td>

                                            <td className="p-2 text-center">
                                                {f.usuario_id ?? "—"}
                                            </td>

                                            <td className="p-2 truncate">
                                                {f.concepto ?? "—"}
                                            </td>

                                            <td className="p-2">
                                                {f.card_brand && f.card_last4
                                                    ? `${f.card_brand.toUpperCase()} **** ${f.card_last4}`
                                                    : f.metodo_pago ?? "—"}
                                            </td>

                                            <td className="p-2 truncate">
                                                {f.card_holder_name ?? "—"}
                                            </td>

                                            <td className="p-2 truncate text-xs">
                                                {f.payment_method_id ?? "—"}
                                            </td>

                                            <td className="p-2 text-center">
                                                {f.card_brand?.toUpperCase() ?? "—"}
                                            </td>

                                            <td className="p-2 text-center">
                                                {f.card_last4 ?? "—"}
                                            </td>

                                            <td className="p-2 truncate text-xs text-center">
                                                {f.stripe_intent_id ?? "—"}
                                            </td>

                                            <td className="p-2 text-center">
                                                {f.estado}
                                            </td>

                                            <td className="p-2 text-right">
                                                {(f.base ?? 0).toFixed(2)}
                                            </td>

                                            <td className="p-2 text-right">
                                                {(f.iva ?? 0).toFixed(2)}
                                            </td>

                                            <td className="p-2 text-right font-semibold">
                                                {(f.cantidad ?? 0).toFixed(2)}
                                            </td>

                                            <td className="p-2 text-center">
                                                {f.factura_pdf ? (
                                                    <a
                                                        href={buildDocumentUrl(f.factura_pdf)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center justify-center px-3 py-1 bg-white hover:bg-blue-700 text-blue-700 hover:text-white border border-blue-700 rounded"
                                                    >
                                                        Ver
                                                    </a>
                                                ) : (
                                                    "—"
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>

                        </table>
                    </div>
                </div>

                {/* Totales */}
                <div className="flex justify-between mt-4">

                    <div className="space-y-2">
                        <div className="text-blue-700">
                            Total facturas: <strong className="text-zinc-800">{facturas.length}</strong>
                        </div>

                        <div className="font-bold grid grid-cols-[1fr_100px] gap-x-2">
                            <span>Base total:</span>
                            <span className="text-right">{totalBase.toFixed(2)} €</span>

                            <span>IVA total:</span>
                            <span className="text-right">{totalIVA.toFixed(2)} €</span>

                            <span>Total:</span>
                            <span className="text-right">{total.toFixed(2)} €</span>
                        </div>
                    </div>

                    <div className="flex items-center justify-center gap-4">
                        <ButtonPopup
                            className="px-4"
                            onClick={() => { if (page <= 1) return; setPage(p => p - 1); fetchFacturas({ page: page - 1 }); }}
                        >
                            ←
                        </ButtonPopup>

                        <span className="mx-2 min-w-[100px] text-center flex-none select-none">
                            Página {page}
                        </span>

                        <ButtonPopup
                            className="px-4"
                            onClick={() => { setPage(p => p + 1); fetchFacturas({ page: page + 1 }); }}
                        >
                            →
                        </ButtonPopup>
                    </div>


                </div>
            </div>
        </div>
    );
}
